"use client";

/**
 * @module Tabs
 *
 * Tabbed interface for switching between content panels. Built on Radix UI Tabs primitive.
 *
 * @see {@link https://ui.shadcn.com/docs/components/tabs Shadcn Tabs}
 * @see {@link https://www.radix-ui.com/primitives/docs/components/tabs Radix Tabs}
 *
 * @example
 * // Basic tabs
 * <Tabs defaultValue="tab1">
 *   <TabsList>
 *     <TabsTrigger value="tab1">Tab 1</TabsTrigger>
 *     <TabsTrigger value="tab2">Tab 2</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="tab1">Content 1</TabsContent>
 *   <TabsContent value="tab2">Content 2</TabsContent>
 * </Tabs>
 *
 * @example
 * // Controlled tabs
 * const [activeTab, setActiveTab] = useState("settings");
 *
 * <Tabs value={activeTab} onValueChange={setActiveTab}>
 *   ...
 * </Tabs>
 */
import { Tabs as TabsPrimitive } from "radix-ui";
import type * as React from "react";
import type { Ref } from "react";
import { cn } from "../lib/utils";

/** Root component that manages tab selection state. */
const Tabs = TabsPrimitive.Root;

/** Container for tab trigger buttons. */
const TabsList = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
  ref?: Ref<React.ElementRef<typeof TabsPrimitive.List>>;
}) => <TabsPrimitive.List className={cn("inline-flex h-10 items-center justify-center rounded-md bg-gray-100 p-1 text-gray-900 dark:bg-gray-900 dark:text-gray-100", className)} ref={ref} {...props} data-sentry-element="TabsPrimitive.List" data-sentry-component="TabsList" data-sentry-source-file="tabs.tsx" />;

/** Button that activates its associated tab content. */
const TabsTrigger = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
  ref?: Ref<React.ElementRef<typeof TabsPrimitive.Trigger>>;
}) => <TabsPrimitive.Trigger className={cn("inline-flex items-center justify-center whitespace-nowrap rounded-xs px-3 py-1.5 font-semibold text-sm ring-offset-gray-100 transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-gray-950 data-[state=active]:shadow-xs dark:ring-offset-gray-900 dark:data-[state=active]:bg-gray-800 dark:data-[state=active]:text-gray-50 dark:focus-visible:ring-[var(--focus-ring)]", className)} ref={ref} {...props} data-sentry-element="TabsPrimitive.Trigger" data-sentry-component="TabsTrigger" data-sentry-source-file="tabs.tsx" />;

/** Content panel shown when its associated tab is active. */
const TabsContent = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & {
  ref?: Ref<React.ElementRef<typeof TabsPrimitive.Content>>;
}) => <TabsPrimitive.Content className={cn("mt-2 ring-offset-gray-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] focus-visible:ring-offset-2 dark:ring-offset-gray-900 dark:focus-visible:ring-[var(--focus-ring)]", className)} ref={ref} {...props} data-sentry-element="TabsPrimitive.Content" data-sentry-component="TabsContent" data-sentry-source-file="tabs.tsx" />;
export { Tabs, TabsContent, TabsList, TabsTrigger };